import React, { useState, useEffect, useCallback } from 'react';
import * as Icons from 'lucide-react';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8092' : `${window.location.protocol}//${window.location.hostname}:8092`;

const UserConversationReport = () => {
  const [dateRange, setDateRange] = useState('7d');
  const [filterType, setFilterType] = useState('all');
  const [stats, setStats] = useState({ total: 0, business: 0, user: 0, avg_response: 'N/A' });
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [statsRes, convRes] = await Promise.all([
        fetch(`${API_BASE}/api/wa/conversations/stats?range=${dateRange}`, { headers }).catch(() => null),
        fetch(`${API_BASE}/api/wa/conversations?type=${filterType}&range=${dateRange}&limit=50`, { headers }).catch(() => null),
      ]);
      if (statsRes?.ok) { const d = await statsRes.json(); setStats(d || {}); }
      if (convRes?.ok) { const d = await convRes.json(); setConversations(d.conversations || []); }
    } catch (err) {
      console.error('Failed to fetch conversation data:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const categoryColors = {
    service: { bg: '#dbeafe', text: '#1e40af' },
    marketing: { bg: '#dcfce7', text: '#166534' },
    utility: { bg: '#fef3c7', text: '#92400e' },
    authentication: { bg: '#f3e8ff', text: '#6b21a8' },
  };

  const statusColors = {
    open: { bg: '#dbeafe', text: '#1e40af' },
    active: { bg: '#dcfce7', text: '#166534' },
    resolved: { bg: '#f3f4f6', text: '#374151' },
    escalated: { bg: '#fee2e2', text: '#991b1b' },
    expired: { bg: '#f3f4f6', text: '#6b7280' },
    auto_closed: { bg: '#f3e8ff', text: '#6b21a8' },
  };

  const getIcon = (name, size = 18) => {
    const Icon = Icons[name];
    return Icon ? <Icon style={{ width: size, height: size }} /> : null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}>
              <Icons.MessageCircle className="w-5 h-5 text-white" />
            </div>
            Conversation Report
          </h1>
          <p className="text-sm text-gray-500 mt-1">Track and analyze your WhatsApp conversations</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-xl border border-gray-200 overflow-hidden">
            {['24h', '7d', '30d', '90d'].map((range) => (
              <button key={range} onClick={() => setDateRange(range)}
                className="px-3 py-2 text-xs font-medium transition-all"
                style={{ background: dateRange === range ? '#25D366' : 'white', color: dateRange === range ? 'white' : '#6b7280' }}
              >{range}</button>
            ))}
          </div>
          <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            <Icons.Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Conversations', value: (stats.total || 0).toLocaleString(), change: stats.total_change, icon: 'MessageCircle', color: '#25D366' },
          { label: 'Business Initiated', value: (stats.business || 0).toLocaleString(), change: stats.business_change, icon: 'Send', color: '#128C7E' },
          { label: 'User Initiated', value: (stats.user || 0).toLocaleString(), change: stats.user_change, icon: 'UserCheck', color: '#34B7F1' },
          { label: 'Avg Response Time', value: stats.avg_response || 'N/A', change: stats.response_change, icon: 'Clock', color: '#075E54' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}14`, color: stat.color }}>
                {getIcon(stat.icon, 20)}
              </div>
              {stat.change && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${String(stat.change).startsWith('+') || stat.change > 0 ? 'text-green-700 bg-green-50' : 'text-blue-700 bg-blue-50'}`}>
                  {stat.change > 0 ? '+' : ''}{stat.change}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Conversation Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Recent Conversations</h3>
          <div className="flex gap-2">
            {['all', 'user_initiated', 'business_initiated'].map((f) => (
              <button key={f} onClick={() => setFilterType(f)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: filterType === f ? 'rgba(37, 211, 102, 0.1)' : 'transparent',
                  color: filterType === f ? '#128C7E' : '#6b7280',
                }}
              >
                {f === 'all' ? 'All' : f === 'user_initiated' ? 'User Initiated' : 'Business Initiated'}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Icons.Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500 mb-3" />
            <p className="text-sm text-gray-500">Loading conversations...</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-12">
            <Icons.MessageCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No Conversations Yet</h3>
            <p className="text-sm text-gray-500">Conversations will appear here when customers interact with your WhatsApp Business number.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-50">
                  <th className="text-left px-4 py-3 font-medium">Contact</th>
                  <th className="text-left px-4 py-3 font-medium">Category</th>
                  <th className="text-left px-4 py-3 font-medium">Type</th>
                  <th className="text-center px-4 py-3 font-medium">Messages</th>
                  <th className="text-left px-4 py-3 font-medium">Agent</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((conv) => (
                  <tr key={conv.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">Contact #{conv.contact_id}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-md text-xs font-medium capitalize" style={{
                        background: categoryColors[conv.category]?.bg || '#f3f4f6',
                        color: categoryColors[conv.category]?.text || '#374151',
                      }}>{conv.category}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 capitalize">{(conv.origin_type || '').replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-gray-700">{conv.total_messages || 0}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">{conv.assigned_agent_id ? `Agent #${conv.assigned_agent_id}` : 'Unassigned'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded-md text-xs font-medium" style={{
                        background: statusColors[conv.status]?.bg || '#f3f4f6',
                        color: statusColors[conv.status]?.text || '#374151',
                      }}>{(conv.status || 'unknown').replace('_', ' ')}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{conv.last_message_at || conv.created_at || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserConversationReport;
